import React, { useRef } from 'react'
import { Block } from '../../../components/UI/Contents'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'

// Components
import { Container, Row, ErrorText, ItemValidation, ValidationsContainer } from './styled'
import { Button } from '../../../components/UI/Buttons'
import Input from '../../UI/TextInput'
import { H2, P } from 'views/components/UI/Text'
import { IItemValidate } from '../Register/constants'
import { Check } from '@mui/icons-material'
import {
  VALIDATE_ALLOWED_PASSWORD_CHARACTERS_REGEX,
  VALIDATE_LOWERCASE_REGEX,
  VALIDATE_SPECIAL_CHARACTERS_REGEX,
  VALIDATE_UPPERCASE_REGEX,
} from 'constants/regex.constants'
import { isValidPassword } from 'constants/password-validation'
import {
  ALERT_PASSWORD_LENGTH,
  ALERT_MAX_LENGTH,
  ALERT_PASSWORD_REQUIRED,
  ALERT_PASSWORD_UPPERCASE,
  ALERT_PASSWORD_LOWERCASE,
  ALERT_PASSWORD_SPECIAL_CHARACTERS,
  ALERT_PASSWORD_CONFIRM,
  ALERT_MIN_LENGTH,
  ALERT_REQUIRED,
} from 'constants/global.constants'
import { useTranslation } from 'react-i18next'

const SignInSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, ALERT_PASSWORD_LENGTH)
    .max(32, ALERT_MAX_LENGTH)
    .required(ALERT_PASSWORD_REQUIRED)
    .matches(VALIDATE_UPPERCASE_REGEX, ALERT_PASSWORD_UPPERCASE)
    .matches(VALIDATE_LOWERCASE_REGEX, ALERT_PASSWORD_LOWERCASE)
    .matches(VALIDATE_SPECIAL_CHARACTERS_REGEX, ALERT_PASSWORD_SPECIAL_CHARACTERS)
    .matches(VALIDATE_ALLOWED_PASSWORD_CHARACTERS_REGEX, 'La contraseña contiene caracteres no permitidos'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], ALERT_PASSWORD_CONFIRM)
    .min(8, ALERT_MIN_LENGTH)
    .max(32, ALERT_MAX_LENGTH)
    .required(ALERT_REQUIRED),
})

interface IProps {
  onSubmit: (values: any) => void
  isProcessing: boolean
}

const ItemValidate = (props: IItemValidate) => {
  const { text, validate } = props

  return (
    <ItemValidation>
      <Check sx={{ color: validate ? '#60CC1E' : '#797A7C' }} />
      <P margin="0px 6px" color={validate ? '#60CC1E' : '#797A7C'}>
        {text}
      </P>
    </ItemValidation>
  )
}

// MAIN
/**
 * @description Form to change password
 */
const ChangePasswordForm = (props: IProps) => {
  const { t } = useTranslation()
  const { onSubmit, isProcessing } = props

  const initialValues = useRef({
    password: '',
    confirmPassword: '',
  })

  // const dispatch = useDispatch()

  const handleSubmit = (values: any): void => {
    onSubmit(values)
  }

  return (
    <Container>
      <H2 marginTop="20px">{t('global.common.recoveryPassword.title')}</H2>

      <Formik
        validateOnChange={true}
        validationSchema={SignInSchema}
        onSubmit={handleSubmit}
        initialValues={initialValues.current}
      >
        {({ errors, touched, values, handleChange, handleBlur }) => (
          <Form style={{ width: '100%' }}>
            <Block display="flex" flexDirection="column">
              <Block m="30px 0px 16px 0px">
                <Input
                  value={values.password}
                  onChange={handleChange('password')}
                  onBlur={handleBlur('password')}
                  type="password"
                  name="password"
                  label={t('global.common.recoveryPassword.inputs.password')}
                  error={Boolean(errors.password && (touched.password || values.password))}
                />
                {errors.password && (touched.password || values.password) ? (
                  <div>
                    <ErrorText>{errors.password}</ErrorText>
                  </div>
                ) : null}
              </Block>
              {errors && (
                <ValidationsContainer>
                  <ItemValidate
                    text={t('global.common.recoveryPassword.requirements.one')}
                    validate={values.password.match(VALIDATE_UPPERCASE_REGEX) ? true : false}
                  />

                  <ItemValidate
                    text={t('global.common.recoveryPassword.requirements.two')}
                    validate={values.password.match(VALIDATE_LOWERCASE_REGEX) ? true : false}
                  />

                  <ItemValidate
                    text={t('global.common.recoveryPassword.requirements.three')}
                    validate={values.password.match(VALIDATE_SPECIAL_CHARACTERS_REGEX) ? true : false}
                  />

                  <ItemValidate
                    text={t('global.common.recoveryPassword.requirements.four')}
                    validate={values.password.length >= 8 && values.password.length <= 32}
                  />

                  <ItemValidate
                    text={t('global.common.recoveryPassword.requirements.five')}
                    validate={VALIDATE_ALLOWED_PASSWORD_CHARACTERS_REGEX.test(values.password)}
                  />
                </ValidationsContainer>
              )}

              <Block m="0px 0px 30px 0px">
                <Input
                  value={values.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  onBlur={handleBlur('confirmPassword')}
                  type="password"
                  name="confirmPassword"
                  label={t('global.common.recoveryPassword.inputs.confirmPassword')}
                  error={Boolean(errors.confirmPassword && (touched.confirmPassword || values.confirmPassword))}
                />
                {errors.confirmPassword && (touched.confirmPassword || values.confirmPassword) ? (
                  <div>
                    <ErrorText>{errors.confirmPassword}</ErrorText>
                  </div>
                ) : null}
              </Block>

              <div style={{ display: 'flex', flexDirection: 'row', verticalAlign: 'middle' }}>
                <Row>
                  <Button
                    style={{
                      width: '100%',
                    }}
                    disabled={!isValidPassword(values.password) || values.password !== values.confirmPassword || isProcessing}
                    type="submit"
                  >
                    {isProcessing ? t('global.common.forgotPassword.buttons.processing') : t('global.common.recoveryPassword.buttons.save')}
                  </Button>
                </Row>
              </div>
            </Block>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

export default ChangePasswordForm
